/**
 * DQ AI chatbot preview — block-aware markdown-lite renderer.
 * Escapes HTML FIRST (server text is never trusted), then renders the bot reply
 * grammar produced by the backend's deterministic renderer:
 *   **bold**, [name](url) links, "- " bullets, | pipe | tables |, ```code fences```.
 * Because the backend renderer has a fixed shape (sections joined by blank lines,
 * a full-bold first line as the section header, the closing question as the last
 * block), this renderer can style headers, option bullets, tables and the final
 * question distinctly — all deterministic, no LLM involvement.
 */

const LINK_STYLE = 'color:#C2410C;font-weight:600;text-decoration:underline;';
const HEADER_STYLE = 'font-weight:700;font-size:14px;margin:0 0 6px;color:var(--brand-text, #1a1a1a);';
const PARA_STYLE = 'margin:2px 0;line-height:1.6;';
const BULLET_ROW_STYLE = 'display:flex;gap:8px;margin:4px 0;line-height:1.55;';
const BULLET_DOT_STYLE = 'color:#FF6900;font-weight:700;flex-shrink:0;';
// A translucent accent rather than a fixed near-white, and an explicit text
// colour from the theme. The flat #FFF7F2 stayed light in dark mode while the
// inherited text turned light with it, leaving the closing question all but
// invisible (Dr. Song's feedback, slide 7). A 10% orange tint reads as the same
// pale peach over a light background and as a faint warm panel over a dark one.
const QUESTION_STYLE = 'margin-top:10px;padding:8px 12px;background:rgba(255,105,0,0.10);'
    + 'color:var(--brand-text, #1a1a1a);'
    + 'border-left:3px solid #FF6900;border-radius:0 8px 8px 0;font-weight:600;line-height:1.55;';
// The same look as a style object, for a React element that shows the chat's closing
// question outside the rendered markdown (the cost card, ChatSidebar ConfirmBuildMessage).
// Keep the two in step.
export const QUESTION_BOX_STYLE = {
    marginTop: '10px',
    padding: '8px 12px',
    background: 'rgba(255,105,0,0.10)',
    color: 'var(--brand-text, #1a1a1a)',
    borderLeft: '3px solid #FF6900',
    borderRadius: '0 8px 8px 0',
    fontWeight: 600,
    lineHeight: 1.55,
};
// The border/radius live on the wrapper (border-collapse ignores border-radius);
// min-width keeps id columns readable — narrow screens scroll horizontally instead
const TABLE_WRAP_STYLE = 'overflow-x:auto;margin:8px 0;border:1px solid var(--brand-border, #F0E6DF);border-radius:10px;';
// The caller sets the min-width: a wide preview page can afford 620px, the 400px
// LearnCreator chat panel needs less before horizontal scrolling stops being usable
const DEFAULT_TABLE_MIN_WIDTH = 620;
const tableStyle = (minWidth) => `border-collapse:collapse;font-size:13px;width:100%;min-width:${minWidth}px;`;
// Same reason as QUESTION_STYLE: a fixed light surface keeps its colour in dark
// mode while the text on it does not, so both are given tokens that follow the
// theme.
const TH_STYLE = 'padding:7px 10px;background:rgba(255,105,0,0.12);color:var(--brand-text, #7A3B00);font-weight:700;'
    + 'text-align:left;border-bottom:1px solid var(--brand-border, #FFE0CC);vertical-align:top;';
const TD_STYLE = 'padding:6px 10px;border-bottom:1px solid var(--brand-border, #F5F0EB);vertical-align:top;';
const CODE_STYLE = 'background:rgba(127,127,127,0.12);border:1px solid var(--brand-border, #E8EAED);'
    + 'color:var(--brand-text, #24292F);border-radius:8px;'
    + 'padding:10px 12px;margin:8px 0;overflow-x:auto;font-family:monospace;'
    + 'font-size:12px;line-height:1.5;white-space:pre;';

const escapeHtml = (text) => String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

// Inline grammar on an already-escaped line: **bold** and [name](url) links
/*
 * A few inline tags survive escaping.
 *
 * The assistant's greeting is authored with <em>...</em> around its example
 * prompts. escapeHtml turns those into &lt;em&gt;, so the reader saw the tags
 * printed as text instead of italics. Only this closed allowlist comes back —
 * everything else stays escaped, so message content still cannot inject
 * markup.
 */
const ALLOWED_INLINE_TAGS = /&lt;(\/?)(em|i|b|strong)&gt;/g;

const inline = (escaped) => escaped
    .replace(ALLOWED_INLINE_TAGS, '<$1$2>')
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(
        /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
        `<a href="$2" target="_blank" rel="noopener noreferrer" style="${LINK_STYLE}">$1</a>`,
    );

const isFullBold = (html) => /^<b>[^<]+<\/b>$/.test(html.trim());
const isBullet = (line) => line.trim().startsWith('- ') || line.trim().startsWith('* ');
const isTableLine = (line) => line.trim().startsWith('|') && (line.match(/\|/g) || []).length >= 2;

const renderTable = (rawLines, minWidth, wrapFirstColumn = false) => {
    const rows = [];
    for (const ln of rawLines) {
        const cells = ln.trim().replace(/^\|+|\|+$/g, '').split('|').map((c) => c.trim());
        // skip separator rows (|---|---|)
        if (cells.length && cells.every((c) => /^[-: ]*$/.test(c))) continue;
        rows.push(cells.map((c) => inline(escapeHtml(c))));
    }
    if (!rows.length) return '';
    /*
     * Column 0 is a short id on the preview page, so it stays on one line
     * there. In the chat it is a framework NAME, and nowrap lets it swallow
     * the table and crush the other columns into vertical, mid-word text.
     *
     * The floor width is what actually stops the crushing: without it a column
     * can be handed less space than a single word.
     */
    const cellStyle = (base, i) => {
        if (i !== 0) return `${base}min-width:120px;`;
        return wrapFirstColumn
            ? `${base}min-width:200px;`
            : `${base}white-space:nowrap;`;
    };
    const th = rows[0].map((c, i) => `<th style="${cellStyle(TH_STYLE, i)}">${c}</th>`).join('');
    const trs = rows.slice(1).map(
        (r) => `<tr>${r.map((c, i) => `<td style="${cellStyle(TD_STYLE, i)}">${c}</td>`).join('')}</tr>`,
    ).join('');
    return `<div style="${TABLE_WRAP_STYLE}"><table style="${tableStyle(minWidth)}">`
        + `<tbody><tr>${th}</tr>${trs}</tbody></table></div>`;
};

// One paragraph block (joined by \n\n on the backend); firstBlock suppresses the
// header treatment for user-authored text that merely starts with bold
const renderBlock = (block, { isFirstLineHeader, minWidth, wrapFirstColumn }) => {
    const lines = block.split('\n');
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const raw = lines[i];
        const t = raw.trim();
        if (t.startsWith('```')) {
            // code fence (e.g. the build payload JSON) — escaped only, no inline grammar
            const code = [];
            i += 1;
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                code.push(escapeHtml(lines[i]));
                i += 1;
            }
            i += 1; // closing fence
            out.push(`<div style="${CODE_STYLE}">${code.join('\n')}</div>`);
            continue;
        }
        if (isTableLine(raw)) {
            const tbl = [];
            while (i < lines.length && isTableLine(lines[i])) {
                tbl.push(lines[i]);
                i += 1;
            }
            out.push(renderTable(tbl, minWidth, wrapFirstColumn));
            continue;
        }
        if (isBullet(raw)) {
            const items = [];
            while (i < lines.length && isBullet(lines[i])) {
                const content = inline(escapeHtml(lines[i].trim().slice(2)));
                items.push(`<div style="${BULLET_ROW_STYLE}">`
                    + `<span style="${BULLET_DOT_STYLE}">&bull;</span><span>${content}</span></div>`);
                i += 1;
            }
            out.push(items.join(''));
            continue;
        }
        // plain line — a full-bold FIRST line of the block is a section header
        const html = inline(escapeHtml(raw));
        if (i === 0 && isFirstLineHeader && isFullBold(html)) {
            out.push(`<div style="${HEADER_STYLE}">${html.trim().slice(3, -4)}</div>`);
        } else if (t) {
            out.push(`<div style="${PARA_STYLE}">${html}</div>`);
        } else {
            out.push('<div style="height:4px;"></div>');
        }
        i += 1;
    }
    return out.join('');
};

export const renderDqAiMarkdown = (text, { tableMinWidth = DEFAULT_TABLE_MIN_WIDTH, wrapFirstColumn = false } = {}) => {
    const blocks = String(text).split('\n\n');
    const rendered = [];
    for (let b = 0; b < blocks.length; b += 1) {
        const block = blocks[b];
        if (!block.trim()) continue;
        const isLast = b === blocks.length - 1;
        const lines = block.split('\n');
        // The closing question: last block, a single plain line ending with "?"
        const isQuestion = isLast && rendered.length > 0 && lines.length === 1
            && !isBullet(block) && !isTableLine(block) && block.trim().endsWith('?');
        if (isQuestion) {
            rendered.push(`<div style="${QUESTION_STYLE}">${inline(escapeHtml(block.trim()))}</div>`);
        } else {
            const html = renderBlock(block, { isFirstLineHeader: true, minWidth: tableMinWidth, wrapFirstColumn });
            rendered.push(`<div style="margin:0 0 8px;">${html}</div>`);
        }
    }
    return rendered.join('');
};

export default renderDqAiMarkdown;
